import { Fragment } from "react"
import { cn } from "@/lib/utils"

// <CheckoutProgress>
//
// Three-step horizontal progress indicator for the checkout flow:
//   1. Create account / Login (label swaps based on `authenticated`)
//   2. Payment details
//   3. Download
//
// Rendered inside <CheckoutLayout>'s `mainHeader` slot. Step items are
// content-sized; dashed connector lines are siblings (not nested inside
// step items), so they span cleanly between circles. On mobile, connectors
// hide and only the active step's label renders — pending/done collapse to
// just the circle.
export function CheckoutProgress({
  step,
  authenticated = false,
  stepTwoLabel = "Payment details",
}: {
  step: 1 | 2 | 3
  authenticated?: boolean
  /** Override for free flows where step 2 isn't a payment (e.g. "Claim license"). */
  stepTwoLabel?: string
}) {
  const steps = [
    { n: 1, label: authenticated ? "Login" : "Create account" },
    { n: 2, label: stepTwoLabel },
    { n: 3, label: "Download" },
  ] as const

  return (
    <div
      role="list"
      aria-label="Checkout progress"
      className="flex items-center justify-between gap-3"
    >
      {steps.map((s, idx) => {
        const state = s.n === step ? "active" : s.n < step ? "done" : "pending"
        return (
          <Fragment key={s.n}>
            <div role="listitem" className="flex min-w-0 items-center gap-3">
              <span
                className={cn(
                  "inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors",
                  state === "active" && "bg-ink-display text-page",
                  state !== "active" && "bg-page text-ink-muted",
                )}
              >
                {state === "done" ? (
                  <svg
                    aria-hidden
                    viewBox="0 0 12 12"
                    className="size-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2.5 6.5 L5 8.75 L9.5 3.75" />
                  </svg>
                ) : (
                  s.n
                )}
              </span>
              <span
                className={cn(
                  "truncate text-sm font-medium",
                  state === "active" ? "inline" : "hidden sm:inline",
                  state === "pending" ? "text-ink-muted" : "text-ink-display",
                )}
              >
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <span
                aria-hidden
                className="hidden h-0 flex-1 border-t border-dashed border-ink-muted/50 sm:block"
              />
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
